import React, { useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Pencil,
  Search,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { segmentedControlClass, segmentedOptionClass } from '../dashboard/themeHelpers';
import {
  PERMISSION_GROUPS,
  PERMISSION_MODULE_HINTS,
  permissionModuleLabel,
  uniquePermissionKeys,
  type PermissionGroupItem,
  type PermissionModuleKey,
  type PermissionRow,
} from '../../config/permissionModules';

export type GroupPreset = 'none' | 'view' | 'edit' | 'full';
export type AccessLevel = GroupPreset;

export interface UserPermissionsFormProps {
  permissions: PermissionRow[];
  onChange: (moduleKey: string, field: 'canView' | 'canEdit' | 'canDelete', value: boolean) => void;
  onRowLevel?: (moduleKey: string, level: AccessLevel) => void;
  onGroupPreset?: (keys: PermissionModuleKey[], preset: GroupPreset) => void;
  loading?: boolean;
}

type PermissionDisplayRow = {
  item: PermissionGroupItem;
  permission: PermissionRow;
  rowKey: string;
};

type AccessFilter = 'all' | 'granted' | 'denied';

const GROUP_ACCENTS: Record<string, string> = {
  Dashboard: 'border-l-indigo-500',
  Communication: 'border-l-violet-500',
  'Work Management': 'border-l-emerald-500',
  'Settings – System': 'border-l-sky-500',
  'Settings – Monitoring': 'border-l-amber-500',
  'Settings – Email Setup': 'border-l-rose-500',
  'Settings – Tools': 'border-l-cyan-500',
  Parameters: 'border-l-slate-500',
};

const GROUP_PRESET_ACTIONS: Array<{
  preset: GroupPreset;
  label: string;
  title: string;
  icon: typeof Eye;
  tone: string;
}> = [
  {
    preset: 'view',
    label: 'View',
    title: 'Grant view only for all modules in this section',
    icon: Eye,
    tone: 'hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-950/40 dark:hover:text-blue-300',
  },
  {
    preset: 'edit',
    label: 'Edit',
    title: 'Grant view + edit for all modules in this section',
    icon: Pencil,
    tone: 'hover:bg-amber-50 hover:text-amber-800 dark:hover:bg-amber-950/40 dark:hover:text-amber-300',
  },
  {
    preset: 'full',
    label: 'Full',
    title: 'Grant view, edit, and delete for all modules in this section',
    icon: ShieldCheck,
    tone: 'hover:bg-emerald-50 hover:text-emerald-800 dark:hover:bg-emerald-950/40 dark:hover:text-emerald-300',
  },
  {
    preset: 'none',
    label: 'Clear',
    title: 'Remove all access for modules in this section',
    icon: X,
    tone: 'hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40 dark:hover:text-red-300',
  },
];

const ACCESS_LEVEL_OPTIONS: Array<{ value: AccessLevel; label: string; short: string }> = [
  { value: 'none', label: 'None', short: '—' },
  { value: 'view', label: 'View', short: 'V' },
  { value: 'edit', label: 'Edit', short: 'E' },
  { value: 'full', label: 'Full', short: 'F' },
];

function GroupPresetToolbar({
  groupTitle,
  moduleKeys,
  onGroupPreset,
}: {
  groupTitle: string;
  moduleKeys: PermissionModuleKey[];
  onGroupPreset: (keys: PermissionModuleKey[], preset: GroupPreset) => void;
}) {
  const { colors } = useTheme();

  return (
    <div
      className={cn('flex shrink-0 items-center', colors.textMuted)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="group"
      aria-label={`Bulk permissions for ${groupTitle}`}
    >
      <div className={cn('inline-flex items-stretch rounded-lg border p-0.5', colors.border, colors.surfaceHover)}>
        {GROUP_PRESET_ACTIONS.map((action, index) => {
          const Icon = action.icon;
          const isClear = action.preset === 'none';
          return (
            <button
              key={action.preset}
              type="button"
              title={action.title}
              onClick={() => onGroupPreset(moduleKeys, action.preset)}
              className={cn(
                'inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors',
                colors.textMuted,
                action.tone,
                isClear && 'text-red-600 dark:text-red-400',
                index > 0 && cn('border-l', colors.border),
              )}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden />
              <span className="hidden sm:inline">{action.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AccessLevelPicker({
  level,
  moduleLabel,
  onChange,
}: {
  level: AccessLevel;
  moduleLabel: string;
  onChange: (level: AccessLevel) => void;
}) {
  const { colors } = useTheme();

  return (
    <div
      className={cn(segmentedControlClass(colors, true), 'w-full sm:w-auto')}
      role="group"
      aria-label={`Access level for ${moduleLabel}`}
    >
      {ACCESS_LEVEL_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            segmentedOptionClass(colors, level === option.value, 'soft', true),
            'flex-1 sm:flex-none min-w-0 px-2 sm:px-2.5',
            option.value === 'none' && level === 'none' && 'text-red-600 dark:text-red-400',
            option.value === 'full' && level === 'full' && 'text-emerald-700 dark:text-emerald-300',
          )}
        >
          <span className="sm:hidden font-semibold">{option.short}</span>
          <span className="hidden sm:inline">{option.label}</span>
        </button>
      ))}
    </div>
  );
}

function groupSummary(rows: PermissionDisplayRow[]) {
  const view = rows.filter((r) => r.permission.canView).length;
  const edit = rows.filter((r) => r.permission.canEdit).length;
  const del = rows.filter((r) => r.permission.canDelete).length;
  return { total: rows.length, view, edit, del };
}

function accessLevel(row: PermissionRow): AccessLevel {
  if (!row.canView) return 'none';
  if (row.canDelete) return 'full';
  if (row.canEdit) return 'edit';
  return 'view';
}

function rowHint(item: PermissionGroupItem): string | undefined {
  return item.hint ?? PERMISSION_MODULE_HINTS[item.moduleKey];
}

function AccessFilterTabs({
  value,
  onChange,
  counts,
}: {
  value: AccessFilter;
  onChange: (value: AccessFilter) => void;
  counts: { all: number; granted: number; denied: number };
}) {
  const { colors } = useTheme();
  const tabs: Array<{ id: AccessFilter; label: string; count: number }> = [
    { id: 'all', label: 'All', count: counts.all },
    { id: 'granted', label: 'With access', count: counts.granted },
    { id: 'denied', label: 'No access', count: counts.denied },
  ];

  return (
    <div className={segmentedControlClass(colors, true)}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          className={cn(segmentedOptionClass(colors, value === tab.id, 'soft', true), 'gap-1.5')}
        >
          {tab.label}
          <span
            className={cn(
              'text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full',
              value === tab.id ? 'bg-white/20' : colors.surfaceHover,
            )}
          >
            {tab.count}
          </span>
        </button>
      ))}
    </div>
  );
}

function SummaryBar({ value, total, tone }: { value: number; total: number; tone: string }) {
  const { colors } = useTheme();
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className={cn('h-1.5 rounded-full overflow-hidden', colors.surfaceHover)}>
      <div className={cn('h-full rounded-full transition-all duration-300', tone)} style={{ width: `${pct}%` }} />
    </div>
  );
}

const UserPermissionsForm: React.FC<UserPermissionsFormProps> = ({
  permissions,
  onChange,
  onRowLevel,
  onGroupPreset,
  loading = false,
}) => {
  const { colors } = useTheme();
  const [search, setSearch] = useState('');
  const [accessFilter, setAccessFilter] = useState<AccessFilter>('all');
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());

  const permissionsByKey = useMemo(() => {
    const map = new Map<string, PermissionRow>();
    permissions.forEach((p) => map.set(p.moduleKey, p));
    return map;
  }, [permissions]);

  const allDisplayRows = useMemo(() => {
    const rows: PermissionDisplayRow[] = [];
    PERMISSION_GROUPS.forEach((group) => {
      group.items.forEach((item) => {
        const permission = permissionsByKey.get(item.moduleKey);
        if (!permission) return;
        rows.push({
          item,
          permission,
          rowKey: `${group.title}:${item.moduleKey}:${item.label}`,
        });
      });
    });
    return rows;
  }, [permissionsByKey]);

  const filterCounts = useMemo(() => {
    const granted = allDisplayRows.filter((r) => r.permission.canView).length;
    return {
      all: allDisplayRows.length,
      granted,
      denied: allDisplayRows.length - granted,
    };
  }, [allDisplayRows]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return PERMISSION_GROUPS.map((group) => {
      const rows: PermissionDisplayRow[] = group.items
        .map((item) => {
          const permission = permissionsByKey.get(item.moduleKey);
          if (!permission) return null;
          const label = item.label.toLowerCase();
          const matchesSearch =
            !q ||
            label.includes(q) ||
            item.moduleKey.toLowerCase().includes(q) ||
            permissionModuleLabel(item.moduleKey).toLowerCase().includes(q) ||
            group.title.toLowerCase().includes(q) ||
            (item.hint?.toLowerCase().includes(q) ?? false);
          if (!matchesSearch) return null;

          const matchesAccess =
            accessFilter === 'all' ||
            (accessFilter === 'granted' && permission.canView) ||
            (accessFilter === 'denied' && !permission.canView);
          if (!matchesAccess) return null;

          return {
            item,
            permission,
            rowKey: `${group.title}:${item.moduleKey}:${item.label}`,
          };
        })
        .filter((row): row is PermissionDisplayRow => row != null);
      return { ...group, rows };
    }).filter((g) => g.rows.length > 0);
  }, [permissionsByKey, search, accessFilter]);

  const toggleGroup = (title: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  const expandAll = () => setOpenGroups(new Set(filteredGroups.map((g) => g.title)));
  const collapseAll = () => setOpenGroups(new Set());

  const applyRowLevel = (moduleKey: string, level: AccessLevel) => {
    if (onRowLevel) {
      onRowLevel(moduleKey, level);
      return;
    }
    if (level === 'none') onChange(moduleKey, 'canView', false);
    else if (level === 'view') {
      onChange(moduleKey, 'canView', true);
      onChange(moduleKey, 'canEdit', false);
      onChange(moduleKey, 'canDelete', false);
    } else if (level === 'edit') {
      onChange(moduleKey, 'canView', true);
      onChange(moduleKey, 'canEdit', true);
      onChange(moduleKey, 'canDelete', false);
    } else {
      onChange(moduleKey, 'canView', true);
      onChange(moduleKey, 'canEdit', true);
      onChange(moduleKey, 'canDelete', true);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className={cn('h-28 rounded-2xl border', colors.border, colors.surface)} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={cn('rounded-2xl border p-4 space-y-4', colors.border, colors.surface)}>
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className={cn('absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4', colors.textMuted)} aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search modules…"
              className={cn(
                'w-full pl-9 pr-3 py-2.5 text-sm rounded-xl border transition-colors',
                colors.border,
                colors.surfaceHover,
                colors.text,
                'focus:outline-none focus:ring-2 focus:ring-primary/30',
              )}
            />
          </div>
          <AccessFilterTabs value={accessFilter} onChange={setAccessFilter} counts={filterCounts} />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={expandAll}
            className={cn('text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors', colors.border, colors.textMuted, colors.interactiveHover)}
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className={cn('text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors', colors.border, colors.textMuted, colors.interactiveHover)}
          >
            Collapse all
          </button>
        </div>
      </div>

      <div className="space-y-4 pb-2">
        {filteredGroups.length === 0 ? (
          <div className={cn('rounded-2xl border px-6 py-12 text-center', colors.border, colors.surface)}>
            <EyeOff className={cn('w-8 h-8 mx-auto mb-3', colors.textMuted)} aria-hidden />
            <p className={cn('text-sm font-medium', colors.text)}>No modules match your filters</p>
            <p className={cn('text-xs mt-1', colors.textMuted)}>Try a different search term or show all modules.</p>
          </div>
        ) : (
          filteredGroups.map((group) => {
            const summary = groupSummary(group.rows);
            const isOpen = openGroups.has(group.title);
            const groupModuleKeys = uniquePermissionKeys(group.items);
            const accent = GROUP_ACCENTS[group.title] ?? 'border-l-slate-400';

            return (
              <section
                key={group.title}
                className={cn('rounded-2xl border border-l-4 overflow-hidden shadow-sm', colors.border, colors.surface, accent)}
              >
                <div className={cn('px-4 py-3 space-y-3', colors.surface)}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.title)}
                      className="flex min-w-0 flex-1 items-start gap-3 text-left"
                    >
                      {isOpen ? (
                        <ChevronDown className={cn('w-4 h-4 shrink-0 mt-0.5', colors.textMuted)} aria-hidden />
                      ) : (
                        <ChevronRight className={cn('w-4 h-4 shrink-0 mt-0.5', colors.textMuted)} aria-hidden />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={cn('font-semibold text-sm', colors.text)}>{group.title}</span>
                          <span className={cn('text-xs px-2 py-0.5 rounded-full', colors.surfaceHover, colors.textMuted)}>
                            {summary.view}/{summary.total} with access
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-2 max-w-xs">
                          <div>
                            <p className={cn('text-[10px] uppercase tracking-wide mb-1', colors.textMuted)}>View</p>
                            <SummaryBar value={summary.view} total={summary.total} tone="bg-blue-500" />
                          </div>
                          <div>
                            <p className={cn('text-[10px] uppercase tracking-wide mb-1', colors.textMuted)}>Edit</p>
                            <SummaryBar value={summary.edit} total={summary.total} tone="bg-amber-500" />
                          </div>
                          <div>
                            <p className={cn('text-[10px] uppercase tracking-wide mb-1', colors.textMuted)}>Delete</p>
                            <SummaryBar value={summary.del} total={summary.total} tone="bg-emerald-500" />
                          </div>
                        </div>
                      </div>
                    </button>
                    {onGroupPreset && (
                      <GroupPresetToolbar
                        groupTitle={group.title}
                        moduleKeys={groupModuleKeys}
                        onGroupPreset={onGroupPreset}
                      />
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className={cn('border-t', colors.border)}>
                    <div
                      className={cn(
                        'hidden md:grid grid-cols-[minmax(0,1fr)_minmax(220px,280px)] gap-4 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider border-b',
                        colors.border,
                        colors.surfaceHover,
                        colors.textMuted,
                      )}
                    >
                      <span>Module</span>
                      <span>Access level</span>
                    </div>
                    <div className={cn('divide-y', colors.border)}>
                      {group.rows.map(({ item, permission, rowKey }) => {
                        const level = accessLevel(permission);
                        const hint = rowHint(item);
                        return (
                          <div
                            key={rowKey}
                            className={cn(
                              'flex flex-col md:grid md:grid-cols-[minmax(0,1fr)_minmax(220px,280px)] md:items-center gap-3 px-4 py-3.5 transition-colors',
                              level !== 'none' && 'bg-primary/[0.03] dark:bg-primary/5',
                            )}
                          >
                            <div className="min-w-0">
                              <p className={cn('text-sm font-medium', colors.text)}>{item.label}</p>
                              {hint && <p className={cn('text-xs mt-0.5 leading-relaxed', colors.textMuted)}>{hint}</p>}
                            </div>
                            <AccessLevelPicker
                              level={level}
                              moduleLabel={item.label}
                              onChange={(next) => applyRowLevel(permission.moduleKey, next)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            );
          })
        )}
      </div>
    </div>
  );
};

export default UserPermissionsForm;
